<template>

    <loading v-if="loading"></loading>
    <div class="page-view--assignment" v-else>
        <header class="page-header assignment-header">

            <router-link :to="returnLink.route" class="back tag button-timid left button-backward">{{ returnLink.name }}</router-link>
            <a href="#" class="assignment-goals tag button-timid right" @click.prevent="showGoals(assignment)">Doelstellingen</a>

            <h1><span class="assignment-icon" :class="type_class"></span> {{ assignment.title }}</h1>

            <progress-bar :assignment="assignment" :steps="steps" :progress="progress" :current-step="currentStepIdx" ></progress-bar>

            <hr>

        </header>

        <transition :css="false" @after-enter="viewChange">
            <router-view :assignment="assignment" :steps="steps" :progress="progress" :group="group" :isInspectingStudent="isInspectingStudent" :isInspecting="isInspecting" :isReviewing="isReviewing" :key="$route.params.stepIdx" :user="user" :returnLink="returnLink"></router-view>
        </transition>

        <teacher-feedback v-if="isReviewing" :assignment="assignment" :progress="progress" :group="group" :isDemo="isDemo" :reviewee="user" :returnLink="returnLink"></teacher-feedback>

    </div>

</template>


<script>
import { fetchAssignment } from 'js/vuex/actions';
import { transform, timestamp, guid } from 'js/vuex/helpers';
import api from  'js/api/resources';
import notify from 'js/components/notifications/notificationsMixin';
import stealth from 'js/components/assignment/stealthMixin';
import redirect from 'js/components/assignment/redirectMixin'

import TeacherFeedback from 'js/components/assignment/TeacherFeedback.vue';
import AssignmentStep from 'js/components/assignment/Step.vue';
import ProgressBar from 'js/components/elements/ProgressBar.vue';
import Loading from 'js/components/elements/Loading.vue';
import { getProgressForUserAssignmentGroup } from 'js/vuex/getters'

import track from 'js/helpers/analytics';

export default {
    name: 'Assignment',
    props: {
        assignmentId: {
            type: String,
            required: true,
        },

        user: {},
        group: {},
        isInspectingStudent: {
            type: Boolean,
            default: false,
        },
        isInspecting: {
            type: Boolean,
            default: false,
        },
        ignoreProgress: {
            type: Boolean,
            default: false,
        },
        isDemo: {
            type: Boolean,
            default: false,
        }
    },
    mixins: [ notify, stealth, redirect ],
    components: { AssignmentStep, ProgressBar, Loading, TeacherFeedback },
    data: function() {
        return {
            loading: false,
        }
    },
    vuex: {
        getters: {
            getProgressForUserAssignmentGroup
        },
        actions: {
            fetchAssignment,
            resetCurrentAssignment: ({dispatch}) => dispatch('RESET_CURRENT_ASSIGNMENT'),
            startAssignment: ({dispatch, state}, assignment) => {

                track.event('assignment', 'start', assignment.id)
                return api.startedAssignment(assignment.id)
                    .then( response => transform(response.body) )
                    .then( result => {
                        dispatch('ADD_PROGRESS', result.progress)
                        dispatch('ADD_PROGRESS_TO_ASSIGNMENT', assignment.id, result.progress[0].id)
                        return result
                    })

            },
            setFakeProgress: ({dispatch}, assignment, user, group) => {

                const now = timestamp();
                const id = guid();

                const progress = {
                    'id': id,
                    'started': now,
                    'submitted': now,
                    'finished': null,
                    'step': assignment.steps.length,
                    'frozen': assignment.steps.length,
                    'assignment': assignment.id,
                    'user': user.id,
                    'group': group.id,
                    'fake': true,
                }

                dispatch('ADD_PROGRESS', [progress])
                dispatch('ADD_PROGRESS_TO_ASSIGNMENT', assignment.id, id)

            },
            showGoals: ({dispatch}, assignment) => {
                track.event('goals', 'show', assignment.id)
                dispatch('SHOW_MODAL', 'Goals', { assignment: assignment })
            }
        }
    },
    computed: {
        assignment: function() {
            return this.$store.state.assignments.storage[this.assignmentId]
        },
        isStarted: function() {
            return this.progress != null && this.progress.started != null
        },
        steps: function() {
            return this.assignment.steps.map( id => this.$store.state.steps.storage[id] )
        },
        progress: function() {
            return this.getProgressForUserAssignmentGroup(this.user.id, this.assignment.id, this.group.id)
        },
        type_class: function() {
            return 'icon-' + this.assignment.type
        },
        currentStepIdx: function() {
            if ( this.$route.params.stepIdx == null ) return this.$route.name;
            return this.$route.params.stepIdx
        },
        isReviewing: function() {
            // logged in user must teach the group, this prevents admins from accidentally giving feedback
            return this.isInspectingStudent && this.$store.state.users.current.groups.indexOf( this.group.id ) !== -1
        },
        returnLink: function() {
            const breadcrumb = this.$store.state.breadcrumbs.previous
            if (breadcrumb) {
                return breadcrumb
            }

            return this.isInspecting
              ? { route: { name: 'students.overview'}, name: 'Voortgang'}
              : { route: { name: 'period' }, name: 'Opdrachten' }
        }
    },
    methods: {
        startIfNecessary: function() {

            if ( this.ignoreProgress && !this.isStarted ) {
                this.setFakeProgress(this.assignment, this.user, this.group)
                return Promise.resolve()
            }

            if (this.isStarted) {
                return Promise.resolve()
            }

            return this.startAssignment(this.assignment)
        },
        checkDisabled: function() {
            if ( this.isInspectingStudent && (this.progress == null || this.progress.finished == null) ) {
                return Promise.reject('Unfinished assignment is disabled')
            }

            if ( this.isDemo && this.assignment.demo !== true ) {
                return Promise.reject('Could not open demo assignment')
            }

        },
        checkRoute: function() {

            const routeParts = this.$route.name.split('.')
            const step = routeParts[routeParts.length-1]
            if ( this.$route.params.stepIdx == null && step !== 'done' &&  step !== 'overview' && step !== 'group' ) {
                this.autoRedirectStep(this.progress)
            }

        },
        viewChange: function () {
            document.getElementById('main-view').scrollTop = 0
        },
    },
    watch: {
        '$route' : function() {
            this.checkRoute()
        }
    },
    created: function() {

        this.loading = true

        const includeUserProgress = this.ignoreProgress ? null : this.user.id

        this.fetchAssignment(this.assignmentId, includeUserProgress)
            .then( () => this.checkDisabled() )
            .then( () => this.startIfNecessary() )
            .then( () => this.loading = false )
            .then( () => this.checkRoute() )
            .catch( err => {
                track.exception('Can\'t load assignment ', err)
                this.$error('Kan opdracht niet laden.')
                this.$router.replace({ name: 'period' })
            });
    },
    beforeDestroy: function() {
        // Needs before destroy, otherwise assignment is reset after new assignment is set (when canReuse = false)
        this.resetCurrentAssignment();
    },

}
</script>


<style lang="scss">

    @import 'global';

    // makes sure step action nav sticks to bottom of screen
    .page-view.page-view--assignment {
        display: flex;
        flex-direction: column;

        .assignment-step {
            flex-grow: 1;
            display: flex;
            flex-direction: column;

            .page-content {
                flex-grow: 1;
            }
        }
    }

    .assignment-header {

        h1 {
            margin-right: 26px; // optical center align with icon
            margin-top: 0.4em;
            margin-bottom: 0 !important;

            .assignment-icon {

                display: inline-block;
                vertical-align: middle;
                border: none;
                margin-right: 2px;

                transform: scale(0.8);

            }
        }

        .progress-bar {
            margin: 4px 0 10px;
        }

    }

    .button-rounded--teachertips {
        float: right;
        display: block;
        margin-left: -$button-rounded-width; // makes sure other elements stay centered
    }

</style>



// WEBPACK FOOTER //
// Assignment.vue?6ac43bd2